import { useState } from "react";
import axios from "axios";
import { Mail, Loader2, Send } from "lucide-react";
import PageShell from "@/components/site/PageShell";
import PageHero from "@/components/site/PageHero";
import { usePageContent } from "@/i18n/pageContent";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Contact() {
  const c = usePageContent("contact");
  const [form, setForm] = useState({ name: "", email: "", message: "" });
  const [status, setStatus] = useState({ state: "idle", message: "" });

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setStatus({ state: "loading", message: "" });
    try {
      await axios.post(`${API}/contact`, {
        name: form.name.trim(),
        email: form.email.trim(),
        message: form.message.trim(),
      });
      setStatus({ state: "success", message: c.success });
      setForm({ name: "", email: "", message: "" });
    } catch (err) {
      const detail = err?.response?.data?.detail || c.errorGeneric;
      setStatus({ state: "error", message: detail });
    }
  };

  return (
    <PageShell>
      <PageHero
        eyebrow={c.eyebrow}
        title={<>{c.titleA} <span className="italic">{c.titleB}</span>.</>}
        subtitle={c.subtitle}
      />
      <section className="bg-white py-20 md:py-28">
        <div className="max-w-7xl mx-auto px-6 md:px-10 grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20">
          <div className="lg:col-span-5 reveal">
            <span className="eyebrow">{c.sideEyebrow}</span>
            <h2 className="font-display text-3xl md:text-5xl text-[#1a365d] mt-4 leading-[1.08] tracking-tight">{c.sideTitle}</h2>
            <p className="mt-6 text-[#475569] leading-[1.85] text-[15.5px]">{c.sideIntro}</p>
            <div className="mt-10 space-y-5">
              <a href="mailto:info@tesorideuropa.com" className="flex items-center gap-4 group">
                <span className="h-11 w-11 inline-flex items-center justify-center border border-[#1a365d] text-[#1a365d] group-hover:bg-[#1a365d] group-hover:text-white transition-colors">
                  <Mail size={16} strokeWidth={1.6} />
                </span>
                <div>
                  <div className="text-[10.5px] tracking-[0.32em] uppercase text-[#475569]">{c.emailLabel}</div>
                  <div className="font-display text-lg md:text-xl text-[#1a365d] group-hover:text-[#d4af37] transition-colors">info@tesorideuropa.com</div>
                </div>
              </a>
            </div>
            <div className="mt-12 border-t border-[#e2e8f0] pt-8">
              <div className="text-[10.5px] tracking-[0.32em] uppercase text-[#475569]">{c.hqLabel}</div>
              <div className="font-display text-2xl text-[#1a365d] mt-2">{c.hqValue}</div>
              <p className="mt-2 text-[#475569] text-sm leading-relaxed">{c.hqSub}</p>
            </div>
          </div>
          <div className="lg:col-span-7 reveal delay-1">
            <form onSubmit={submit} className="bg-[#fafaf7] p-8 md:p-12 border-l-2 border-[#d4af37]">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <label className="block">
                  <span className="text-[10.5px] tracking-[0.32em] uppercase text-[#475569]">{c.name}</span>
                  <input type="text" required minLength={2} value={form.name} onChange={set("name")} data-testid="contact-name"
                    className="mt-2 w-full bg-transparent border-b border-[#1a365d]/30 focus:border-[#d4af37] outline-none py-2 text-[#1a365d] font-body" />
                </label>
                <label className="block">
                  <span className="text-[10.5px] tracking-[0.32em] uppercase text-[#475569]">{c.email}</span>
                  <input type="email" required value={form.email} onChange={set("email")} data-testid="contact-email"
                    className="mt-2 w-full bg-transparent border-b border-[#1a365d]/30 focus:border-[#d4af37] outline-none py-2 text-[#1a365d] font-body" />
                </label>
              </div>
              <label className="block mt-8">
                <span className="text-[10.5px] tracking-[0.32em] uppercase text-[#475569]">{c.message}</span>
                <textarea required minLength={5} rows={6} value={form.message} onChange={set("message")} data-testid="contact-message"
                  className="mt-2 w-full bg-transparent border-b border-[#1a365d]/30 focus:border-[#d4af37] outline-none py-2 text-[#1a365d] font-body resize-none" />
              </label>
              <button type="submit" disabled={status.state === "loading"} data-testid="contact-submit"
                className="btn-primary mt-10 disabled:opacity-60 disabled:cursor-not-allowed">
                {status.state === "loading" ? (<><Loader2 size={14} className="animate-spin" /> {c.submitting}</>) : (<>{c.submit} <Send size={14} strokeWidth={1.8} /></>)}
              </button>
              {status.message && (
                <p data-testid="contact-feedback" className={`mt-5 text-sm font-body ${status.state === "success" ? "text-[#1a365d]" : "text-[#9b1c1c]"}`}>{status.message}</p>
              )}
            </form>
          </div>
        </div>
      </section>
    </PageShell>
  );
}
